import React, { useEffect, useRef, useState } from 'react';
import { useStore } from 'react-redux';
import AddCommentIcon from '@mui/icons-material/AddComment';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import DrawIcon from '@mui/icons-material/Draw';
import ErrorIcon from '@mui/icons-material/Error';
import LibraryAddIcon from '@mui/icons-material/LibraryAdd';
import MinimizeIcon from '@mui/icons-material/Minimize';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import SendIcon from '@mui/icons-material/Send';
import StopIcon from '@mui/icons-material/Stop';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { AssistantModelName } from '@paragon/contract-library';

import { sendAssistantMessage } from '../../assistant/assistantSession';
import {
  ChatEntry,
  closeAssistantChat,
  decidePlan,
  openAssistantChat,
  requestStop,
  resetConversation,
  setAssistantMinimized,
  setAssistantModel,
  setAutoApprovePlans,
  useAssistantState,
  useChat,
} from '../../assistant/assistantStore';
import {
  Attachment,
  ATTACHMENT_ACCEPT,
  AttachmentError,
  attachmentSize,
  MAX_ATTACHMENT_BYTES_PER_MESSAGE,
  MAX_ATTACHMENTS_PER_MESSAGE,
  readAttachment,
} from '../../assistant/attachments';
import { showPreview } from '../../assistant/planPreview';
import { PREVIEW_COLORS, PreviewChange } from '../../assistant/planPreviewRequest';
import { describeSelectedItem } from '../../assistant/snapshot';
import type { RootState } from '../../store';
import { getSelectedItemEntries } from '../../store/selectors/getSelectedItemSelector';
import { useAppDispatch, useAppSelector } from '../hooks';
import { FeedbackEntry, RecordingBar } from './AssistantFeedback';
import { AssistantMarkdown } from './AssistantMarkdown';
import { SketchDialog } from './SketchDialog';

function ToolEntry({ entry }: { entry: Extract<ChatEntry, { type: 'tool' }> }) {
  const icon =
    entry.status === 'running' ? (
      <CircularProgress size={14} />
    ) : entry.status === 'done' ? (
      <CheckCircleIcon fontSize="small" color="success" />
    ) : (
      <ErrorIcon fontSize="small" color="error" />
    );

  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', color: 'text.secondary' }}>
      {icon}
      <Typography variant="body2" component="span" sx={{ fontFamily: 'monospace' }}>
        {entry.toolName}
      </Typography>
      {entry.detail && (
        <Typography variant="body2" component="span" color="error" noWrap title={entry.detail}>
          {entry.detail}
        </Typography>
      )}
    </Stack>
  );
}

const PREVIEW_CHANGES: readonly PreviewChange[] = ['add', 'remove', 'change'];
const PREVIEW_LABELS: Record<PreviewChange, string> = { add: 'New', remove: 'Removed', change: 'Changed' };

// The legend's swatches, hatched as the view draws each kind of change.
const stripes = (angle: number, color: string) =>
  `repeating-linear-gradient(${angle}deg, ${color} 0 1.5px, transparent 1.5px 4px)`;
const HATCHES: Record<PreviewChange, (color: string) => string> = {
  add: (color) => stripes(45, color),
  change: (color) => stripes(-45, color),
  remove: (color) => `${stripes(45, color)}, ${stripes(-45, color)}`,
};

function PlanEntry({
  chatId,
  entry,
  pending,
}: {
  chatId: string;
  entry: Extract<ChatEntry, { type: 'plan' }>;
  pending: boolean;
}) {
  const dispatch = useAppDispatch();
  const [feedback, setFeedback] = useState('');
  const { plan, decision } = entry;
  const preview = pending ? plan.preview : null;
  const changes = PREVIEW_CHANGES.filter((change) => preview?.shapes.some((shape) => shape.change === change));

  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Typography variant="subtitle2">Plan</Typography>
      <Typography variant="body2" sx={{ mb: 1 }}>
        {plan.summary}
      </Typography>
      <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
        {plan.steps.map((step, index) => (
          <Typography component="li" variant="body2" key={index}>
            {step}
          </Typography>
        ))}
      </Box>

      {preview && (changes.length > 0 || preview.notFound.length > 0) && (
        <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', flexWrap: 'wrap', mt: 1 }}>
          {changes.map((change) => (
            <Stack key={change} direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
              <Box
                sx={{
                  width: 14,
                  height: 10,
                  border: 1,
                  borderStyle: change === 'remove' ? 'dashed' : 'solid',
                  borderColor: PREVIEW_COLORS[change],
                  background: HATCHES[change](PREVIEW_COLORS[change]),
                }}
              />
              <Typography variant="caption">{PREVIEW_LABELS[change]}</Typography>
            </Stack>
          ))}
          {preview.notFound.length > 0 && (
            <Typography variant="caption" color="text.secondary">
              Not shown: {preview.notFound.join(', ')}
            </Typography>
          )}
          {changes.length > 0 && (
            <Button size="small" sx={{ ml: 'auto' }} onClick={() => showPreview(preview, dispatch)}>
              Show on view
            </Button>
          )}
        </Stack>
      )}

      {pending && (
        <Stack spacing={1} sx={{ mt: 1.5 }}>
          <TextField
            size="small"
            placeholder="Changes to the plan (optional)"
            value={feedback}
            onChange={(event) => setFeedback(event.target.value)}
            multiline
            maxRows={4}
          />
          <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
            <Button
              size="small"
              onClick={() =>
                decidePlan(chatId, { approved: false, ...(feedback.trim() ? { feedback: feedback.trim() } : {}) })
              }
            >
              Reject
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={() =>
                decidePlan(chatId, { approved: true, ...(feedback.trim() ? { feedback: feedback.trim() } : {}) })
              }
            >
              Approve
            </Button>
          </Stack>
        </Stack>
      )}

      {decision && (
        <Typography variant="caption" color={decision.approved ? 'success.main' : 'text.secondary'}>
          {decision.approved ? (decision.automatic ? 'Approved automatically' : 'Approved') : 'Rejected'}
          {decision.feedback ? `: ${decision.feedback}` : ''}
        </Typography>
      )}
    </Paper>
  );
}

/**
 * Fixes the Assistant offered for a problem it explained. Trying one sends it as the designer's next message, so it goes
 * through a Plan like anything else.
 */
function FixesEntry({
  entry,
  onTry,
}: {
  entry: Extract<ChatEntry, { type: 'fixes' }>;
  onTry: ((request: string) => void) | null;
}) {
  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Typography variant="subtitle2">Possible fixes</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {entry.problem}
      </Typography>
      <Stack spacing={1}>
        {entry.fixes.map((fix, index) => (
          <Stack key={index} direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Typography variant="body2" sx={{ flexGrow: 1 }}>
              {fix.label}
            </Typography>
            <Button
              size="small"
              variant="outlined"
              disabled={onTry == null}
              onClick={() => onTry?.(fix.request)}
              title={fix.request}
            >
              Try it
            </Button>
          </Stack>
        ))}
      </Stack>
    </Paper>
  );
}

function Entry({
  chatId,
  entry,
  pendingPlan,
  onTry,
}: {
  chatId: string;
  entry: ChatEntry;
  pendingPlan: boolean;
  onTry: ((request: string) => void) | null;
}) {
  switch (entry.type) {
    case 'user':
      return (
        <Paper
          elevation={0}
          sx={{
            alignSelf: 'flex-end',
            maxWidth: '85%',
            px: 1.5,
            py: 1,
            bgcolor: 'action.selected',
            whiteSpace: 'pre-wrap',
          }}
        >
          <Typography variant="body2">{entry.text}</Typography>
          {entry.attachments && (
            <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', mt: 0.5 }}>
              {entry.attachments.map((attachment, index) => (
                <Chip key={index} size="small" icon={<AttachFileIcon />} label={attachment.name} />
              ))}
            </Stack>
          )}
        </Paper>
      );
    case 'assistant':
      return <AssistantMarkdown text={entry.text} />;
    case 'tool':
      return <ToolEntry entry={entry} />;
    case 'plan':
      return <PlanEntry chatId={chatId} entry={entry} pending={pendingPlan} />;
    case 'fixes':
      return <FixesEntry entry={entry} onTry={onTry} />;
    case 'notice':
      return (
        <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
          {entry.text}
        </Typography>
      );
    case 'feedback':
      // Rendered by the window, which knows the entry's index.
      return null;
  }
}

/**
 * The Assistant's question while it waits for the designer to click in the view.
 */
function PointRequest({ question, onCancel }: { question: string; onCancel: () => void }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderColor: 'primary.main' }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
        <TouchAppIcon color="primary" fontSize="small" />
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="body2">{question}</Typography>
          <Typography variant="caption" color="text.secondary">
            Click in the view to answer.
          </Typography>
        </Box>
        <Button size="small" onClick={onCancel}>
          Cancel
        </Button>
      </Stack>
    </Paper>
  );
}

/**
 * Says what's selected, since it goes with every message: "this web" means the selected one.
 */
function SelectionLine() {
  const entries = useAppSelector(getSelectedItemEntries);
  const names = entries.map((entry) => {
    const described = describeSelectedItem(entry);
    return 'name' in described ? described.name : described.type;
  });
  if (names.length === 0) return null;

  const shown = names.length > 4 ? `${names.slice(0, 4).join(', ')} and ${names.length - 4} more` : names.join(', ');
  return (
    <Typography variant="caption" color="text.secondary" noWrap title={names.join(', ')} sx={{ px: 1.5, pt: 1 }}>
      Selected: {shown}
    </Typography>
  );
}

const PANEL_WIDTH = 420;
const PANEL_GAP = 12;

/**
 * One of the Assistant's chat windows: its conversation, Plan approval, and Stop. Windows line up from the right, the
 * first chat rightmost.
 */
export function AssistantPanel({ chatId, position }: { chatId: string; position: number }) {
  const dispatch = useAppDispatch();
  const store = useStore<RootState>();

  const open = useAssistantState((state) => state.open);
  const minimized = useChat(chatId, (chat) => chat.minimized);
  const entries = useChat(chatId, (chat) => chat.entries);
  const running = useChat(chatId, (chat) => chat.running);
  const stopRequested = useChat(chatId, (chat) => chat.stopRequested);
  const pendingPlan = useChat(chatId, (chat) => chat.pendingPlan);
  const pendingPoint = useChat(chatId, (chat) => chat.pendingPoint);
  const model = useChat(chatId, (chat) => chat.model);
  const autoApprovePlans = useAssistantState((state) => state.autoApprovePlans);

  // Name the window after what the designer asked first, so windows can be told apart.
  const firstRequest = entries.find((entry) => entry.type === 'user');
  const title = firstRequest?.type === 'user' ? firstRequest.text : 'Assistant';

  const [draft, setDraft] = useState('');
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [sketching, setSketching] = useState(false);
  const [sketchCount, setSketchCount] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const attachFiles = async (files: readonly File[]) => {
    setAttachmentError(null);
    const room = MAX_ATTACHMENTS_PER_MESSAGE - attachments.length;
    if (files.length > room) setAttachmentError(`Attach at most ${MAX_ATTACHMENTS_PER_MESSAGE} files to a message.`);

    let totalBytes = attachments.reduce((total, attachment) => total + attachmentSize(attachment), 0);
    const read: Attachment[] = [];
    for (const file of files.slice(0, Math.max(room, 0))) {
      if (totalBytes + Math.min(file.size, MAX_ATTACHMENT_BYTES_PER_MESSAGE) > MAX_ATTACHMENT_BYTES_PER_MESSAGE) {
        setAttachmentError(
          `${file.name} would take this message past ${MAX_ATTACHMENT_BYTES_PER_MESSAGE / 1024 / 1024} MB of attachments.`,
        );
        continue;
      }
      try {
        const attachment = await readAttachment(file);
        totalBytes += attachmentSize(attachment);
        read.push(attachment);
      } catch (error) {
        setAttachmentError(error instanceof AttachmentError ? error.message : `Couldn’t read ${file.name}.`);
      }
    }
    setAttachments((current) => [...current, ...read].slice(0, MAX_ATTACHMENTS_PER_MESSAGE));
  };

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [entries.length, minimized]);

  const canSend = draft.trim() !== '' || attachments.length > 0;

  const send = () => {
    if (!canSend || running) return;
    void sendAssistantMessage(chatId, draft, attachments, dispatch, store.getState);
    setDraft('');
    setAttachments([]);
    setAttachmentError(null);
  };

  // The last Plan entry is the one waiting for approval, if any is.
  const pendingPlanIndex = pendingPlan == null ? -1 : entries.findLastIndex((entry) => entry.type === 'plan');

  return (
    <Paper
      elevation={8}
      onDragOver={(event) => event.preventDefault()}
      onDrop={(event) => {
        event.preventDefault();
        void attachFiles(Array.from(event.dataTransfer.files));
      }}
      sx={{
        display: open ? 'flex' : 'none',
        flexDirection: 'column',
        position: 'fixed',
        // Windows that don't fit beside the others stay on screen, over the leftmost one.
        right: {
          xs: 8,
          sm: `min(${16 + position * (PANEL_WIDTH + PANEL_GAP)}px, calc(100vw - ${PANEL_WIDTH + 16}px))`,
        },
        bottom: { xs: 8, md: 48 },
        width: { xs: 'calc(100% - 16px)', sm: PANEL_WIDTH },
        height: minimized ? 'auto' : { xs: 'calc(100% - 16px)', md: 'min(720px, calc(100% - 140px))' },
        overflow: 'hidden',
        zIndex: (theme) => theme.zIndex.drawer + 1,
      }}
    >
      <Stack
        direction="row"
        spacing={0.5}
        sx={{
          alignItems: 'center',
          pl: 2,
          pr: 0.5,
          py: 0.5,
          borderBottom: minimized ? 0 : 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="subtitle1" noWrap title={title} sx={{ minWidth: 0 }}>
          {title}
        </Typography>
        <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', pl: 1 }}>
          {minimized && running && <CircularProgress size={14} aria-label="Working" />}
        </Box>
        <Select
          size="small"
          variant="standard"
          value={model}
          disabled={running}
          onChange={(event) => setAssistantModel(chatId, event.target.value as AssistantModelName)}
          inputProps={{ 'aria-label': 'Effort' }}
        >
          {/* Designers pick how hard the Assistant works, not a model: Haiku, Sonnet, and Opus underneath. */}
          <MenuItem value="haiku">Low</MenuItem>
          <MenuItem value="sonnet">Medium</MenuItem>
          <MenuItem value="opus">High</MenuItem>
        </Select>
        <Tooltip title={autoApprovePlans ? 'Plans are approved automatically' : 'Approve plans automatically'}>
          <IconButton
            aria-label="Approve plans automatically"
            aria-pressed={autoApprovePlans}
            color={autoApprovePlans ? 'primary' : 'default'}
            onClick={() => setAutoApprovePlans(!autoApprovePlans)}
          >
            <DoneAllIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="New conversation">
          <span>
            <IconButton aria-label="New conversation" disabled={running} onClick={() => resetConversation(chatId)}>
              <AddCommentIcon />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="New chat window">
          <IconButton aria-label="New chat window" onClick={() => openAssistantChat()}>
            <LibraryAddIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title={minimized ? 'Expand' : 'Minimize'}>
          <IconButton
            aria-label={minimized ? 'Expand' : 'Minimize'}
            onClick={() => setAssistantMinimized(chatId, !minimized)}
          >
            {minimized ? <OpenInFullIcon fontSize="small" /> : <MinimizeIcon />}
          </IconButton>
        </Tooltip>
        <IconButton aria-label="Close" onClick={() => closeAssistantChat(chatId)}>
          <CloseIcon />
        </IconButton>
      </Stack>

      <Stack
        ref={scrollRef}
        spacing={1.5}
        sx={{ display: minimized ? 'none' : 'flex', flexGrow: 1, overflowY: 'auto', p: 2 }}
      >
        {entries.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            Describe what you want, such as “Lay out a 24×20 house with a 6/12 gable roof and design the trusses.” The
            Assistant shows its plan before it changes anything.
          </Typography>
        )}
        {entries.map((entry, index) =>
          entry.type === 'feedback' ? (
            <FeedbackEntry key={index} chatId={chatId} index={index} entry={entry} />
          ) : (
            <Entry
              key={index}
              chatId={chatId}
              entry={entry}
              pendingPlan={index === pendingPlanIndex}
              onTry={
                running ? null : (request) => void sendAssistantMessage(chatId, request, [], dispatch, store.getState)
              }
            />
          ),
        )}
        {pendingPoint && <PointRequest question={pendingPoint.question} onCancel={pendingPoint.cancel} />}
        {running && pendingPlan == null && pendingPoint == null && (
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', color: 'text.secondary' }}>
            <CircularProgress size={14} />
            <Typography variant="caption">{stopRequested ? 'Stopping after this step…' : 'Working…'}</Typography>
          </Stack>
        )}
      </Stack>

      {!minimized && (
        <Box sx={{ px: 1.5, pb: 1 }}>
          <RecordingBar chatId={chatId} />
        </Box>
      )}

      {!minimized && <SelectionLine />}

      {!minimized && (attachments.length > 0 || attachmentError) && (
        <Stack spacing={0.5} sx={{ px: 1.5, pt: 1, borderTop: 1, borderColor: 'divider' }}>
          {attachments.length > 0 && (
            <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap' }}>
              {attachments.map((attachment, index) => (
                <Chip
                  key={index}
                  size="small"
                  icon={<AttachFileIcon />}
                  label={attachment.name}
                  onDelete={() => setAttachments((current) => current.filter((_, other) => other !== index))}
                />
              ))}
            </Stack>
          )}
          {attachmentError && (
            <Typography variant="caption" color="error">
              {attachmentError}
            </Typography>
          )}
        </Stack>
      )}

      <Stack
        direction="row"
        spacing={1}
        sx={{
          display: minimized ? 'none' : 'flex',
          alignItems: 'flex-end',
          p: 1.5,
          borderTop: 1,
          borderColor: 'divider',
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={ATTACHMENT_ACCEPT}
          multiple
          hidden
          onChange={(event) => {
            void attachFiles(Array.from(event.target.files ?? []));
            event.target.value = '';
          }}
        />
        <Tooltip title="Attach images, PDFs, or text files">
          <span>
            <IconButton aria-label="Attach files" disabled={running} onClick={() => fileInputRef.current?.click()}>
              <AttachFileIcon />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Sketch on the view">
          <span>
            <IconButton
              aria-label="Sketch on the view"
              disabled={running || attachments.length >= MAX_ATTACHMENTS_PER_MESSAGE}
              onClick={() => setSketching(true)}
            >
              <DrawIcon />
            </IconButton>
          </span>
        </Tooltip>
        <TextField
          fullWidth
          size="small"
          multiline
          maxRows={6}
          placeholder="Message the Assistant"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onPaste={(event) => {
            const files = Array.from(event.clipboardData.files);
            if (files.length === 0) return;
            event.preventDefault();
            void attachFiles(files);
          }}
          onKeyDown={(event) => {
            // Keep the app's hotkeys from firing while typing.
            event.stopPropagation();
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              send();
            }
          }}
        />
        {running ? (
          <Tooltip title="Stop after the current step">
            <span>
              <IconButton aria-label="Stop" color="error" disabled={stopRequested} onClick={() => requestStop(chatId)}>
                <StopIcon />
              </IconButton>
            </span>
          </Tooltip>
        ) : (
          <IconButton aria-label="Send" color="primary" disabled={!canSend} onClick={send}>
            <SendIcon />
          </IconButton>
        )}
      </Stack>
      <SketchDialog
        open={sketching}
        sketchNumber={sketchCount + 1}
        onClose={() => setSketching(false)}
        onAttach={(sketch) => {
          setSketchCount((count) => count + 1);
          setAttachments((current) => [...current, sketch].slice(0, MAX_ATTACHMENTS_PER_MESSAGE));
        }}
      />
    </Paper>
  );
}
